<script setup lang="ts">
import AgilaInfoBlock from "frontend-component-templates/src/components/agilaInfoBlock.vue";
import TableColumnHeadComponent from "../../components/sections/TableColumnHeadComponent.vue";
import { Product } from "../../classes/product";
import { defineEmits, defineProps, withDefaults, ref } from "vue";
import { ProductGroup } from "../../classes/productGroup";

interface Props {
  product?: Product;
  selectedDate?: Date;
  liability: boolean;
  defaultProduct: number;
  productGroup: ProductGroup;
  sticky?: boolean;
  noHeaderVisible?: boolean;
  highlightDefaultProduct: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  product: undefined,
  selectedDate: undefined,
  liability: false,
  defaultProduct: undefined,
  productGroup: undefined,
  sticky: true,
  noHeaderVisible: false,
  highlightDefaultProduct: false,
});

const emit = defineEmits<{
  (e: "selectProduct", product: Product): void;
}>();

const noHeaderVisible = ref(false);

document.addEventListener("scroll", () => {
  const header = document
    .querySelector("#header > div")
    ?.shadowRoot?.querySelector("div > header");
  if (header) {
    const headerStyle = window.getComputedStyle(header);
    noHeaderVisible.value = headerStyle.display === "none";
  } else {
    noHeaderVisible.value = true;
  }
});
</script>

<template>
  <div
    class="table-head"
    :class="{
      sticky: props.sticky,
      'no-header': noHeaderVisible,
      'hps-combi': props.productGroup?.products?.length < 3,
      'single-product': props.productGroup?.products?.length === 1,
      liability: props.liability,
    }"
  >
    <div
      class="table-head-hint"
      :class="{ 'display-none': props.productGroup?.priceIsFinal() }"
    >
      <agila-info-block :small="true"
        >Ohne Rasse- und Altersangabe sehen Sie den
        Einstiegspreis</agila-info-block
      >
    </div>
    <div
      class="table-head-column"
      :class="{
        'non-highlight-column':
          highlightDefaultProduct &&
          props.defaultProduct !== 0 &&
          props.defaultProduct !== index + 1,
      }"
      v-for="(product, index) in props.productGroup.products"
      :key="product.id"
      :data-cy="'product-head-' + product.identifier"
    >
      <template v-if="props.defaultProduct === index + 1">
        <div class="table-head-column__recommendation desktop">
          Unsere Empfehlung
        </div>
        <div class="table-head-column__recommendation mobile">Empfehlung</div>
      </template>
      <TableColumnHeadComponent
        :product="product"
        :selectedDate="props.selectedDate"
        :liability="props.liability"
        :defaultProduct="props.defaultProduct"
        :index="index"
        @selectProduct="emit('selectProduct', product)"
      ></TableColumnHeadComponent>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-head {
  grid-area: c;
  background-color: var(--color-white);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas: "a a a" "b c d";
  z-index: 2;

  &.liability {
    grid-template-columns: 1fr 1fr;

    .table-head-column__recommendation {
      opacity: 0 !important;
      height: 0;
      padding: 0;
    }
    .table-head-column-con {
      border-radius: 16px 16px 0 0 !important;
      border-left: 1px solid var(--color-gray-500);
      border-top: 1px solid var(--color-gray-500);
      color: var(--color-gray-900);
    }
  }

  > div:nth-of-type(2) {
    grid-area: b;
  }

  > div:nth-of-type(3) {
    grid-area: c;
  }
}
</style>
